/**
 * Accrual close-pack capability (H4) — browser-safe.
 *
 * No Worker HTTP, no `@gv/contracts`. The admin portal imports this module
 * because it cannot import `accrual_pack.ts` (HttpError) or Python. Shipping
 * H3 flips {@link ./accrual_close_pack.ts}; every TypeScript consumer of
 * {@link operationalAccrualPackReady} lifts together.
 *
 * Python twin: `pipeline/accrual_pack.py`, bound by
 * `examine_accrual_pack_language_drift` (three sites: this module, Python,
 * portal `operationalAccrualWritesAllowed`).
 *
 * `declarations` substitutes the close-pack module for tests. Production
 * call sites omit it so a test double cannot leak into a live path.
 * {@link operationalAccrualPackReady} has no substitution argument.
 */
import {
  HUB_UNSETTLED_COA_LEAF,
  postHubUnsettledAccrualAjes,
} from "./accrual_close_pack";
import { opsLogLine } from "./http";

/** Tracking issue every refusal names. Brief: cite #604 or the close-pack design. */
export const ACCRUAL_PACK_ISSUE = "#604" as const;

export const ACCRUAL_PACK_TRACKED =
  "docs/audits/design-review-accrual-completeness.md H3" as const;

export const REQUIRED_UNSETTLED_COA_LEAF = "Hub:Unsettled" as const;

export const CLOSE_WRITER_NAME = "postHubUnsettledAccrualAjes" as const;

export type CompanyBooksBasisParse =
  | { ok: true; basis: "cash" | "accrual"; examined: string }
  | {
      ok: false;
      outcome: "missing_basis" | "unparseable_basis";
      examined: string;
    };

export type AccrualPackExamination = {
  ready: boolean;
  writerPresent: boolean;
  coaMembership: boolean;
  examined: string;
};

/**
 * Fail-closed books-basis parse. Missing or unparseable → refuse and print
 * what was examined.
 */
export function parseCompanyBooksBasis(
  raw: unknown,
  examined: string,
): CompanyBooksBasisParse {
  if (raw === null || raw === undefined || raw === "") {
    const line = `ACCRUAL_PACK_REFUSED outcome=missing_basis examined=${examined}`;
    opsLogLine("info", line);
    return { ok: false, outcome: "missing_basis", examined };
  }
  if (raw === "cash" || raw === "accrual") {
    return { ok: true, basis: raw, examined };
  }
  const shown = typeof raw === "string" ? raw : `type=${typeof raw}`;
  const fullExamined = `${examined} value=${shown}`;
  opsLogLine("info",
    `ACCRUAL_PACK_REFUSED outcome=unparseable_basis examined=${fullExamined}`,
  );
  return { ok: false, outcome: "unparseable_basis", examined: fullExamined };
}

export type AccrualPackDeclarations = {
  writer: unknown;
  leaf: unknown;
};

/**
 * Writer-present predicate. Production reads {@link postHubUnsettledAccrualAjes}
 * from the close-pack declaration module. Tests may substitute that
 * declaration; production call sites omit the argument.
 */
export function closeWriterPresent(
  writer: unknown = postHubUnsettledAccrualAjes,
): boolean {
  return typeof writer === "function";
}

/**
 * Declared CoA leaf predicate. Production reads {@link HUB_UNSETTLED_COA_LEAF}
 * from the close-pack declaration module. Tests may substitute that
 * declaration; production call sites omit the argument.
 */
export function unsettledCoaDeclared(
  leaf: unknown = HUB_UNSETTLED_COA_LEAF,
): boolean {
  return leaf === REQUIRED_UNSETTLED_COA_LEAF;
}

/**
 * Capability check: writer present + CoA membership. Going-forward writers
 * and the portal consult this. Shipping H3 flips {@link ./accrual_close_pack.ts}
 * (and the Python close module); the writers stay.
 *
 * `declarations` substitutes the close-pack module for tests. Production
 * omits it so a test double cannot leak into a live path.
 */
export function examineAccrualPack(
  examined: string,
  declarations?: AccrualPackDeclarations,
): AccrualPackExamination {
  const writer = declarations?.writer ?? postHubUnsettledAccrualAjes;
  const leaf = declarations?.leaf ?? HUB_UNSETTLED_COA_LEAF;
  const writerPresent = closeWriterPresent(writer);
  const coaMembership = unsettledCoaDeclared(leaf);
  const packExamined =
    `${examined} writer_present=${String(writerPresent)} ` +
    `writer=${CLOSE_WRITER_NAME} coa_unsettled=${String(coaMembership)} ` +
    `coa_leaf=${REQUIRED_UNSETTLED_COA_LEAF} ` +
    `issue=${ACCRUAL_PACK_ISSUE} tracked=${ACCRUAL_PACK_TRACKED}`;
  return {
    ready: writerPresent && coaMembership,
    writerPresent,
    coaMembership,
    examined: packExamined,
  };
}

/**
 * Live capability. No default argument, no query-param, no declarations
 * bag — production and the portal both call this with zero arguments.
 */
export function operationalAccrualPackReady(): boolean {
  return examineAccrualPack("capability").ready;
}
